import { JobMode, TokenSub } from './crawler.enums';

export enum ControlListOptions {
    None = 'none',
    Whitelist = 'whitelist',
    Blacklist = 'blacklist'
}

export interface CrawlfishJob {
    id: number;
    created_at: string;
    updated_at: string;
    status: string;
    info: JobInfo;
    requested_uris: string[];
    sources: string[];
    keywords?: JobKeywords;
    reviewed: boolean;
    rules?: JobRules;
    name: string;
    owner: string;
}

export interface JobsRequestResponse {
    ResultCount: ResultCount;
    Results?: CrawlfishJob[];
}

export interface PagesRequestResponse {
    ResultCount: ResultCount;
    Results?: Page[];
}
export interface CrawlfishJobWithPages extends CrawlfishJob {
    pages?: Page[];
}
export interface Page {
    id: number;
    job_id: number;
    domain: string;
    exported?: string;
    source: string;
    uri: string;
    query: string;
    links: number;
    depth: number;
    keywords?: PageKeyword[];
    score: number;
    type_scores?: PageTypeScores;
    status: string;
    info?: PageInfo;
    error?: PageError;
    duration: string;
    selected: boolean;
    created_at: string;
    updated_at: string;
    deleted_at?: string | null;
}

export interface PageError {
    msg?: string;
}

export interface PageKeyword {
    id: number;
    job_id: number;
    page_id: number;
    term: string;
    location: string;
    count: number;
    deleted_at?: string | null;
}

export interface PageInfo {
    redirect_page_id: number;
    redirect_url: string;
}

export interface PageTypeScores {
    home?: number;
    privacy?: number;
    terms?: number;
    financing?: number;
    checkout?: number;
    product?: number;
    directory?: number;
    faq?: number;
    about?: number;
    blog?: number;
    email?: number;
}
export interface JobKeywords {
    body: string[];
    images?: string[] | null;
}

export interface CreateCrawlfishJob {
    job_name: string;
    job_owner: string;
    domains: DomainAndSource[];
    keywords: string;
    rules: JobRules;
}

export interface DomainAndSource {
    domain: string;
    source: string;
}
export interface JobInfo {
    detail?: string;
    elapsed?: string;
    cost?: JobCost
    pages_complete?: number;
    pages_failed?: number;
    pages_other?: number;
    pages_queued?: number;
    pages_redirected?: number;
    pages_total?: number;
    selected?: number;
    typed_pages?: string;
    domain_counts?: DomainCount[];
    domains_failed?: number;
    unique_domains?: number;
}

export interface JobCost {
    ai: number;
}

export interface DomainCount {
    domain: string;
    count: number;
}

export interface JobInfoState {
    activeColumn: string;
    job_name: string;
    job_id: number;
    info: JobInfo;
    isLoading: boolean;
    errorMessage: string;
    sortIndex: number;
}

export interface JobRules {
    prevent_reverse_crawl: boolean;
    max_page_count: number;
    max_crawl_depth: number;
    max_ai_input_tokens?: number;
    mode?: JobMode;
    ai_typing?: boolean;
    path_control?: ControlListOptions;
    control_paths?: string[];
    footer_keywords?: boolean;
}

export interface JobsDataState {
    ResultCount: ResultCount;
    loadedJobs: CrawlfishJob[];
    filteredJobs: CrawlfishJob[];
    activeFilters: ActiveFilter[];
    emptyResultsMessage?: JSX.Element | null;
    isFilterInProgress: boolean;
    sortInfo: {
        sortIndex: number,
        activeColumn: string,
    },
    activePage?: number;
}

export interface JobListModalState {
    isCreateJobModalOpen: boolean;
    isRecreateJobModalOpen: boolean;
    isWorkersModalOpen: boolean;
}

export interface ActiveFilter {
    name: string;
    value: string;
    confidenceLevel?: string;
}
export interface ParamType {
    [k: string]: any;  // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface User {
    username: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    primary_group_id: string;
    last_login: string;
    legacy_user_id: number;
    force_secrets_change: boolean;
    region: string;
    last_active_company: string;
    last_active_brand: string;
    current_role: string;
    crawlfish_user: boolean;
    original_username: string;
    role_group_ids?: any;  // eslint-disable-line @typescript-eslint/no-explicit-any
    from_pline: boolean;
    first_name: string;
    last_name: string;
    email: string;
    phone_number?: string;
    company: string;
    show_web_pro_analytics: string;
    show_ccm_pro_analytics: string;
    show_social_pro_analytics: string;
    show_messaging_pro_analytics: string;
    show_email_pro_analytics: string;
    show_document_pro_analytics: string;
    show_benchmarking_pro_analytics: string;
    tableau_username: string;
    pro_analytics_site_root: string;
    opt_out_remediation_change_owner_email: boolean;
    help_on: boolean;
    teams?: any;    // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface JobError {
    job_id: number;
    page_id: number;
    domain: string;
    uri: string;
    message: string;
    created_at: string;
    updated_at: string;
    deleted_at?: null
}

export interface ResultCount {
    Total: number;
    Current: number;
    Limit: number;
    Offset: number;
}

export interface Pagination {
    offset: number;
}

export type TokenSubType = TokenSub.Access | TokenSub.Refresh;

export interface AccessToken {
    aud: string[];      // ['cf']
    exp: number;
    iat: number;
    iss: string;        // pl-accounts
    nbf: string;
    sub: TokenSubType;  // access
    usr: string;        // username@performline.com
}

export interface RefreshToken {
    aud: string[];      // ['ac']
    exp: number;
    iat: number;
    iss: string;        // pl-accounts
    nbf: string;
    sub: TokenSubType;  // refresh
    usr: string;        // username@performline.com
}